/**
 * CSV export (Phase 4 — reporting). Small, dependency-free: turn a matrix of
 * string cells into an RFC-4180-ish CSV blob and trigger a browser download.
 *
 * Escaping rule (RFC 4180): a field is wrapped in double quotes iff it contains a
 * comma, a double quote, or a newline; embedded double quotes are doubled. We quote
 * conservatively (also a leading/trailing space, and a leading =,+,-,@ to defuse
 * spreadsheet formula injection) so values round-trip cleanly into Excel/Sheets.
 */
const NEEDS_QUOTING = /[",\r\n]/;
const FORMULA_LEAD = /^[=+\-@]/;

export function escapeCsvCell(value: unknown): string {
  const s = value === null || value === undefined ? "" : String(value);
  // Defuse spreadsheet formula injection: a cell starting with = + - @ is prefixed
  // with a single quote so a spreadsheet treats it as text, never a live formula.
  const guarded = FORMULA_LEAD.test(s) ? `'${s}` : s;
  if (NEEDS_QUOTING.test(guarded) || guarded !== guarded.trim()) {
    return `"${guarded.replace(/"/g, '""')}"`;
  }
  return guarded;
}

/** Serialize a header row + body rows to a CSV string (CRLF line endings). */
export function toCsv(header: string[], rows: Array<Array<unknown>>): string {
  const lines = [header, ...rows].map((row) => row.map(escapeCsvCell).join(","));
  return lines.join("\r\n");
}

/**
 * Trigger a browser download of a CSV file. Builds a Blob + object URL + a synthetic
 * anchor, then ALWAYS revokes the URL (an un-revoked object URL leaks until unload —
 * same discipline as the GDPR JSON export).
 */
export function downloadCsv(filename: string, header: string[], rows: Array<Array<unknown>>): void {
  // Prepend a UTF-8 BOM so Excel reads non-ASCII (emails, names) correctly.
  const blob = new Blob(["﻿", toCsv(header, rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
